import { atom, selectorFamily } from 'recoil'
import { DEFAULT_SLIPPAGE } from './components/BoxHeader/BoxHeader'
//const { persistAtom } = recoilPersist() // not currently used

export interface Pool {
    id: number
    name: string
    token1: string
    address: string
    token1Amount: string // dynamic TODO: | undefined
    token2: string
    token2Amount: string // dynamic TODO: | undefined
    fee: number
    shares: string
    cfmm: number
    liq_token: string
    // refreshAt: Map<string, number> // map from field to its refresh time
}

export interface CLPoolKey {
    token1: string // lower address
    token2: string // higher address
    fee: number
}

export interface CLPool {
    address: string
    token1: Token
    token2: Token
    fee: number
    tickSpacing: number

    currentPrice?: string
    currentTick?: number
}

export interface SwapQuote {
    pool: CLPool

    // this amount can be input or output, depending on exactInput flag
    // and could be token1 or token2, depending on forwardSwap flag
    requestedAmountWei: string

    // the following tokens are same as the POOL tokens:
    token1AmountWei?: string // amount*10^decimals
    token1Amount: string // "1.000" format
    token2AmountWei?: string // amount*10^decimals
    token2Amount: string // "1.000" format
    forwardSwap: boolean // false if POOL token2->token1 swap
    // (which basically means that token1AmountWei is for pool.token2,
    //  since the UI is always token1->token2 swap...)
    exactInput: boolean // true if the quote is for the target token given src token amount
    // othewise it's a quote for src token given target token amount
    err?: string

    blockNumber?: number // block number of quote
    poolPriceBefore?: string // pool price before the swap
    poolPriceAfter?: string // pool price after the swap
}

// export interface Pool {
//     // id: number
//     name: string
//     token1: string
//     token2: string
//     address: string
//     fee: number
// }

// static Token data
export interface Token {
    network: string
    name: string
    symbol: string
    address: string
    decimals: number
    id: number
    icon: string // url
    is_bluechip: boolean
}

// dynamic Token data
export interface TokenUserData {
    address: string
    ammBalance: string | undefined
    userBalance: string | undefined
    userApproved: string | undefined
    refreshAt: Map<string, number>
}

export interface Transaction {
    hash: string
    network: string
    type: string
    token1: string
    token1Amount: string
    token2: string
    token2Amount: string
    status: string
    dismiss: boolean // TODO : remove
    pool_id?: number
    error?: string
    labelFields?: { timestampSent: number; sentCurrentTick?: number }
    // TODO add acceptance time
}

export interface ToastMsg {
    msg: string
    type: string
    dismiss: boolean
    hash: string // for uniqueness
}

export interface NumAndNetwork {
    pool_id: number
    network: string
}

export interface TokenAddr {
    addr: string
    network: string
}

export const poolsState = atom({
    key: 'poolsState',
    default: new Map<string, Pool>()
})
export const poolKey = (key: NumAndNetwork): string => {
    return `${key.pool_id}@${key.network}`
}

export const tokensState = atom({
    key: 'tokensState',
    default: new Map<string, Token>()
})
export const tokenKey = (key: TokenAddr): string => {
    return `${key.addr}@${key.network}`
}

export const tokensUserDataState = atom({
    key: 'tokensUserDataState',
    default: new Map<string, TokenUserData>()
})

export const pendingTxsState = atom({
    key: 'pendingTxsState',
    default: new Array<Transaction>()
})

export const toastsState = atom({
    key: 'toastsState',
    default: new Array<ToastMsg>()
})

export const lpBalanceState = atom({
    key: 'lpBalanceState',
    default: new Map<string, string>()
})

export const swapSlippage = atom({
    key: 'swapSlippage',
    default: DEFAULT_SLIPPAGE
})

export const addLiquiditySlippage = atom({
    key: 'addLiquiditySlippage',
    default: DEFAULT_SLIPPAGE
})

export const withdrawLiquiditySlippage = atom({
    key: 'withdrawLiquiditySlippage',
    default: DEFAULT_SLIPPAGE
})

export const numOfPoolsPerNetworkState = atom({
    key: 'numOfPoolsPerNetworkState',
    default: new Map<string, number>()
})

// Selectors

export const numOfPoolsSelector = selectorFamily({
    key: 'numOfPoolsSelector',
    get:
        (networkName: string) =>
        ({ get }): number => {
            const numPools: Map<string, number> = get(numOfPoolsPerNetworkState)
            return (numPools.has(networkName) && numPools.get(networkName)) || 0
        }
})

export const transactionsStatusSelector = selectorFamily({
    key: 'transactionsStatusSelector',
    get:
        (componentName: string) =>
        ({ get }): Array<Transaction> | undefined => {
            const pendingTx = get(pendingTxsState)
            if (pendingTx) return pendingTx.filter((t) => t.type === componentName)
            return undefined
        }
})

/*
export const walletAmountsSelector = selector({
    key: 'walletAmountsSelector',
    get: ({ get }): Map<string, string> => {
        let r = new Map<string, string>()
        get(tokensUserDataState).forEach((tud, address) => {
            if (tud.userBalance !== undefined) {
                let token = get(tokensState).get(address)
                if (token && token.decimals) {
                    r.set(address, tud.userBalance)
                }
            }
        })
        return r
    }
})

export const walletAllowanceSelector = selector({
    key: 'walletAllowanceSelector',
    get: ({ get }): Map<string, string> => {
        let r = new Map<string, string>()
        get(tokensUserDataState).forEach((tud, address) => {
            if (tud.userApproved !== undefined) {
                let token = get(tokensState).get(address)
                if (token && token.decimals) {
                    r.set(address, tud.userApproved)
                }
            }
        })
        return r
    }
})
*/

export const txsForNetworkSelector = selectorFamily({
    key: 'txsForNetworkSelector',
    get:
        (network: string) =>
        ({ get }): Array<Transaction> => {
            let r = Array<Transaction>()
            get(pendingTxsState).forEach((tx: Transaction) => {
                if (tx.network === network) r.push(tx)
            })
            return r
        }
})

export const pendingTxSelector = selectorFamily({
    key: 'pendingTxSelector',
    get:
        (network: string) =>
        ({ get }): Map<string, string> => {
            let r = new Map<string, string>()
            get(txsForNetworkSelector(network)).forEach((tx) => {
                r.set(tx.hash, tx.status)
            })
            return r
        }
})

export const lastTxDoneSelector = selectorFamily({
    key: 'lastTxDoneSelector',
    get:
        (network: string) =>
        ({ get }): boolean => {
            let done = true
            get(txsForNetworkSelector(network)).forEach((tx) => {
                done = false
                if (tx.status === 'REJECTED' || tx.status.includes('ACCEPTED')) done = true
            })
            return done
        }
})

export const safeToSendTxSelector = selectorFamily({
    key: 'safeToSendTxSelector',
    get:
        (network: string) =>
        ({ get }): boolean => {
            const n = get(txsForNetworkSelector(network)).length
            if (n === 0) return true

            const tx = get(pendingTxsState)[n - 1]
            if (tx.status.includes('REJECTED') || tx.status.includes('ACCEPTED') || tx.status.includes('PENDING')) return true

            return false
        }
})
